/**
 * Plain-TypeScript shapes for the LLM model registry. The registry is static,
 * in-repo data, so model shapes are expressed as TypeScript types and defaults
 * are applied in plain code (`models.ts`) rather than through a validation
 * library. Its one runtime dependency is `moment`, used by `lifecycle.ts` for
 * UTC-correct plain-date math (the repo's date convention). See the package
 * README for the longer-term plan (single source of truth + generated JSON
 * Schema for non-TypeScript consumers).
 */

export enum LlmProvider {
  OpenAI = 'openai',
  Anthropic = 'anthropic',
  Google = 'google',
  Meta = 'meta',
  Amazon = 'amazon',
  DeepSeek = 'deepseek',
}

/** Content types a model can accept (input) or produce (output). */
export enum LlmModelModality {
  Text = 'Text',
  File = 'File',
  Image = 'Image',
}

/**
 * Stable model identifiers. The string VALUES are persisted in saved Flow
 * Builder block configs (`config.modelName`), so they MUST NOT change — only
 * their home moves into this package.
 *
 * The member KEYS intentionally keep `snake_case` rather than the usual
 * `camelCase`, for three reasons: they are migrated verbatim from the former
 * `web/vivaa-api` `llmPrompt/config.ts` enum (so no consumer has to churn); they
 * mirror the equivalent model enums elsewhere in the system (`backend/flow/api`,
 * `backend/predict/documents`), keeping the names aligned across the codebase as
 * this package grows toward the single source of truth; and they encode dotted
 * version numbers (e.g. `gpt_5_4`, `gemini_2_5_pro`) where the underscore is the
 * only separator that keeps `2_5` (2.5) from collapsing into an ambiguous `25` —
 * camelCasing would defeat the clarity the naming convention exists to protect.
 */
export enum LlmModelName {
  amazon_nova_pro = 'amazon.nova-pro-v1:0',
  claude_3_5_sonnet_v2 = 'anthropic.claude-3-5-sonnet-20241022-v2:0',
  claude_3_7_sonnet_v1 = 'anthropic.claude-3-7-sonnet-20250219-v1:0',
  claude_opus_4 = 'anthropic.claude-opus-4',
  claude_sonnet_4 = 'anthropic.claude-sonnet-4',
  deepseek_r1 = 'deepseek-r1',
  deepseek_v3 = 'deepseek-ai/DeepSeek-V3',
  gemini_1_5_pro = 'gemini-1.5-pro-002',
  gemini_1_5_flash = 'gemini-1.5-flash-002',
  gemini_2_0_flash_001 = 'gemini-2.0-flash-001',
  gemini_2_0_flash_thinking_exp_01_21 = 'gemini-2.0-flash-thinking-exp-01-21',
  gemini_2_5_flash = 'gemini-2.5-flash',
  gemini_2_5_flash_lite = 'gemini-2.5-flash-lite',
  gemini_2_5_pro_preview_03_25 = 'gemini-2.5-pro-preview-03-25',
  gemini_2_5_pro = 'gemini-2.5-pro',
  gemini_3_pro_preview = 'gemini-3-pro-preview',
  gemini_3_flash_preview = 'gemini-3-flash-preview',
  gemini_3_1_pro_preview = 'gemini-3.1-pro-preview',
  gemini_3_1_flash_lite_preview = 'gemini-3.1-flash-lite-preview',
  gpt_4_1 = 'gpt-4.1',
  gpt_4_1_mini = 'gpt-4.1-mini',
  gpt_4_1_nano = 'gpt-4.1-nano',
  gpt_4o = 'gpt-4o',
  gpt_4o_mini = 'gpt-4o-mini',
  gpt_5 = 'gpt-5',
  gpt_5_mini = 'gpt-5-mini',
  gpt_5_nano = 'gpt-5-nano',
  gpt_5_1 = 'gpt-5.1',
  gpt_5_2 = 'gpt-5.2',
  gpt_5_4 = 'gpt-5.4',
  gpt_5_4_mini = 'gpt-5.4-mini',
  gpt_5_4_nano = 'gpt-5.4-nano',
  claude_opus_4_1 = 'anthropic.claude-opus-4-1-20250805-v1:0',
  claude_opus_4_5 = 'anthropic.claude-opus-4-5-20251015-v1:0',
  claude_haiku_4_5 = 'anthropic.claude-haiku-4-5-20251015-v1:0',
  claude_sonnet_4_5 = 'anthropic.claude-sonnet-4-5-20250929-v1:0',
  claude_opus_4_6 = 'anthropic.claude-opus-4-6-v1',
  claude_sonnet_4_6 = 'anthropic.claude-sonnet-4-6',
  meta_llama_3_2_11b = 'meta.llama3-2-11b-instruct-v1:0',
  meta_llama_3_3_70b = 'meta.llama3-3-70b-instruct-v1:0',
  o1 = 'o1',
  o3 = 'o3',
  o1_mini = 'o1-mini',
  o3_mini = 'o3-mini',
  o4_mini = 'o4-mini',
  notable_default = 'notable-default',
}

/** Lifecycle state, derived at runtime from a model's retired/obsolete dates. */
export enum LlmModelLifecycleState {
  Active = 'Active',
  Retired = 'Retired',
  Obsolete = 'Obsolete',
}

/**
 * Model family aliases (mirrors flow-api `MODEL_FAMILIES`). A model belongs to
 * a family, and when it is retired/obsolete it falls back to its successor
 * family's current model.
 */
export enum LlmModelFamily {
  GeminiPro = 'geminiPro',
  GeminiFlash = 'geminiFlash',
  GeminiFlashLite = 'geminiFlashLite',
  ClaudeOpus = 'claudeOpus',
  ClaudeSonnet = 'claudeSonnet',
  ClaudeHaiku = 'claudeHaiku',
  Gpt = 'gpt',
  GptMini = 'gptMini',
  GptNano = 'gptNano',
  O = 'o',
  OMini = 'oMini',
  AmazonNova = 'amazonNova',
  DeepseekReasoning = 'deepseekReasoning',
  DeepseekChat = 'deepseekChat',
  MetaLlama = 'metaLlama',
  NotableDefault = 'notableDefault',
}

/** Whether a model supports a given API parameter. */
export enum LlmParameterSupport {
  Supported = 'supported',
  Unsupported = 'unsupported',
}

/**
 * Temperature support. Some models accept `temperature` and `top_p`
 * individually but not together; `ExcludesTopP` means temperature is supported
 * but must not be combined with top_p.
 */
export enum LlmTemperatureSupport {
  Supported = 'supported',
  Unsupported = 'unsupported',
  ExcludesTopP = 'excludesTopP',
}

/** Top-p support; the mirror of {@link LlmTemperatureSupport} for `top_p`. */
export enum LlmTopPSupport {
  Supported = 'supported',
  Unsupported = 'unsupported',
  ExcludesTemperature = 'excludesTemperature',
}

/**
 * Per-parameter support matrix (OpenRouter-aligned names). A model declares only
 * the parameters it supports; every other field is filled in as `Unsupported`
 * when the registry is materialized (see `DEFAULT_SUPPORTED_PARAMETERS` in
 * `models.ts`). `Unsupported` is the safe default: the failure mode is
 * under-utilizing a capability, never sending a parameter the model rejects.
 */
export interface LlmSupportedParameters {
  temperature: LlmTemperatureSupport;
  topP: LlmTopPSupport;
  tools: LlmParameterSupport;
  toolChoice: LlmParameterSupport;
  maxTokens: LlmParameterSupport;
  reasoning: LlmParameterSupport;
  includeReasoning: LlmParameterSupport;
  structuredOutputs: LlmParameterSupport;
  responseFormat: LlmParameterSupport;
  stop: LlmParameterSupport;
  frequencyPenalty: LlmParameterSupport;
  presencePenalty: LlmParameterSupport;
  seed: LlmParameterSupport;
}

export interface LlmModelLifecycle {
  /** Date the model entered its `Retired` window (still callable); null if not retired. */
  retiredDate: string | null;
  /** Date the model's endpoint shuts down (no longer callable); null if none announced. */
  obsoleteDate: string | null;
  /**
   * Curation override: treat the model as `Obsolete` regardless of its dates —
   * dropped from selection, and (once the backend consumes this registry) routed
   * to its successor family instead of being called, exactly like a date-obsolete
   * model.
   */
  forceObsolete?: boolean;
  /** Family whose current model this one falls back to once obsolete (best-effort). */
  successorModelFamily?: LlmModelFamily;
}

/** A fully-resolved registry entry (defaults applied). */
export interface LlmModel {
  id: LlmModelName;
  /** Human-readable display label. */
  name: string;
  /** The provider we route this model through; absent for meta-models like "Auto". */
  provider?: LlmProvider;
  /** `YYYY-MM-DD` the model was added to this registry (best-effort). */
  addedDate?: string;
  inputModalities: LlmModelModality[];
  outputModalities: LlmModelModality[];
  supportedParameters: LlmSupportedParameters;
  lifecycle: LlmModelLifecycle;
  /** Artificial Analysis Intelligence Index; the tier is derived from this. */
  intelligenceScore?: number;
  modelFamily?: LlmModelFamily;
  /** Provider-specific deployment/version identifier, when distinct from `id`. */
  releaseVersion?: string;
  contextLength?: number;
  maxOutputTokens?: number;
  /** Ordered fallbacks for resource-exhaustion (HTTP 429/529) scenarios. */
  resourceExhaustionFallbackChain?: LlmModelName[];
}

/**
 * The authoring shape. `supportedParameters` and `lifecycle` may be given
 * partially (or omitted) and are completed with defaults — see `resolveModel`.
 */
export interface LlmModelInput extends Omit<LlmModel, 'supportedParameters' | 'lifecycle'> {
  supportedParameters?: Partial<LlmSupportedParameters>;
  lifecycle?: Partial<LlmModelLifecycle>;
}
